// src/tenantAdmin/hooks/useWorkspaceCompany.js
//
// The company that owns a given workspace — when the caller ADMINISTERS that company — for the Utenti tab's
// company-admins section (v202). THREE states, because the host renders different things for them:
//   `undefined` — still looking it up: render NOTHING in that slot (showing the read-only block and then swapping
//                 it for the section would flash a mode change at every workspace switch);
//   `null`      — no company, a company the user does not administer, or a failed lookup (fail closed);
//   an id       — the company to manage.
//
// ⛔ Why a network read: `TenantInfo` carries NO `customer_id`, so the claims cannot say which company owns a
// workspace. `GET /credits/balance?tenant=<id>` answers it for any workspace the caller holds a claim on (the
// same read useManagedCompany makes for the ACTIVE workspace — here it is the SELECTED one, which may differ).
// Answers are remembered per workspace for the life of the component (a workspace does not change company
// mid-session), so switching back is instant. A failed read is remembered as "no company" (fail closed: the
// read-only block) until the tab is opened again.
// ⛔ `isCustomerAdminOf` gates the answer: a company the user does not administer would make every
// /admin/customers/{cid}* call answer 403 CUSTOMER_ADMIN_REQUIRED, which raises the permissions-drift banner.
// No company the user administers → no read at all.
import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { fetchCreditBalance } from '../../services/api/creditsApi.js';

export default function useWorkspaceCompany(tenantId) {
  const { customers, isCustomerAdminOf } = useAuth();
  // No company claim with role admin → nothing to look up, and the answer is "no section" at once.
  const administersAny = (customers || []).some(c => c.role === 'admin');
  // { [tenantId]: customerId | null } — null = looked up, no company (or the read failed this time).
  const [known, setKnown] = useState({});

  const settled = tenantId ? Object.prototype.hasOwnProperty.call(known, tenantId) : true;

  useEffect(() => {
    if (!tenantId || !administersAny || settled) return undefined;
    let cancelled = false;
    // Inside a promise chain, so a synchronous throw or a stub returning nothing lands in "no company".
    Promise.resolve()
      .then(() => fetchCreditBalance({ tenant: tenantId }))
      .then(b => { if (!cancelled) setKnown(prev => ({ ...prev, [tenantId]: b?.customer_id || null })); })
      .catch(() => { if (!cancelled) setKnown(prev => ({ ...prev, [tenantId]: null })); });
    return () => { cancelled = true; };
  }, [tenantId, administersAny, settled]);

  if (!tenantId || !administersAny) return null;
  if (!settled) return undefined;
  const companyId = known[tenantId];
  return companyId && isCustomerAdminOf?.(companyId) ? companyId : null;
}
